import { animate, state, style, transition, trigger } from "@angular/animations";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { EproductService } from "../../e-commerce/ecommerce-product/ecommerce-product.service";

/** Shape the hosts (discount, cart rules) keep in their own model and post back. */
export interface SelectedProduct {
  id: any;
  name: string;
  isPublished: boolean;
}

@Component({
  selector: "app-product-selector",
  templateUrl: "./product-selector.component.html",
  styleUrls: ["./product-selector.component.css"],
  animations: [
    trigger("detailExpand", [
      state("collapsed", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition(
        "expanded <=> collapsed",
        animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")
      ),
    ]),
  ],
})
export class ProductSelectorComponent implements OnInit, OnDestroy {
  /** DOM id the host button targets with data-target. */
  @Input() modalId = "productSelector";
  @Input() title = "Add products";
  @Input() hint = "Pick the products this rule applies to.";

  /**
   * The host selection array. It is mutated in place so hosts that read it
   * straight off their model (discount.Allproducts, cartRule.products) keep
   * working; selectedChange is emitted as well for hosts that prefer binding.
   */
  @Input() selected: SelectedProduct[] = [];
  @Output() selectedChange = new EventEmitter<SelectedProduct[]>();

  displayedColumns: string[] = ["Selected", "Name", "IsAllowed", "IsPublished"];
  dataSource = new MatTableDataSource<any>([]);
  dataLength = 0;
  pageIndex = 0;
  pageSize = 10;

  searchText = "";
  categories: any[] = [];
  selectedCategoryIds: any[] = [];

  expandedElement: any = null;
  loading = false;
  bulkLoading = false;

  /** Chips rendered before the tray collapses behind a "+N more" toggle. */
  readonly chipLimit = 12;
  trayExpanded = false;

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private eProductService: EproductService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((text) => {
        this.searchText = text;
        this.pageIndex = 0;
        this.loadProducts();
      });

    this.loadCategories();
    this.loadProducts();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------- loading

  loadProducts() {
    this.loading = true;
    this.eProductService
      .getAllProductsList({
        pagination: { pageSize: this.pageSize, pageNumber: this.pageIndex },
        searchText: this.searchText || "",
        categoryIds: this.selectedCategoryIds || [],
      })
      .subscribe(
        (res: any) => {
          this.dataSource = new MatTableDataSource<any>(res.data || []);
          this.dataLength = res.totalCount || 0;
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  loadCategories() {
    this.eProductService.getAllCategories().subscribe((res: any) => {
      const categories = (res || []).map((element) => {
        const categoryId = element.id;
        return { ...element, ...element.categoryDetails[0], id: categoryId };
      });
      this.categories = this.flatten(categories, null);
    });
  }

  private flatten(data: any[], parentId: any = null, level = 0): any[] {
    let flattened = [];
    data.forEach((item) => {
      if (item.parentId === parentId) {
        item.arrow = parentId ? "▸".repeat(level) : "";
        item.level = level;
        flattened.push(item);
        flattened = flattened.concat(this.flatten(data, item.id, level + 1));
      }
    });
    return flattened;
  }

  // ---------------------------------------------------------------- filters

  onSearch(text: string) {
    this.search$.next(text || "");
  }

  onCategoryChange() {
    this.pageIndex = 0;
    this.loadProducts();
  }

  clearFilters() {
    this.searchText = "";
    this.selectedCategoryIds = [];
    this.pageIndex = 0;
    this.loadProducts();
  }

  get hasFilters(): boolean {
    return !!this.searchText || !!(this.selectedCategoryIds || []).length;
  }

  changePage(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProducts();
  }

  // -------------------------------------------------------------- selection

  private sameId(a: any, b: any): boolean {
    return String(a) === String(b);
  }

  isSelected(product: any): boolean {
    return this.selected.some((item) => this.sameId(item.id, product.id));
  }

  private add(product: any) {
    if (this.isSelected(product)) {
      return;
    }
    this.selected.push({
      id: product.id,
      name: product.defaultName || product.name,
      isPublished: product.isPublished,
    });
  }

  private drop(product: any) {
    const index = this.selected.findIndex((item) =>
      this.sameId(item.id, product.id)
    );
    if (index > -1) {
      this.selected.splice(index, 1);
    }
  }

  /** A product and its variations are selected and cleared together. */
  private family(product: any): any[] {
    const variations = product.variations || [];
    return [product, ...variations];
  }

  setSelected(product: any, shouldSelect: boolean) {
    this.family(product).forEach((item) =>
      shouldSelect ? this.add(item) : this.drop(item)
    );
    this.selectedChange.emit(this.selected);
  }

  toggle(product: any) {
    this.setSelected(product, !this.isSelected(product));
  }

  /** Parent row is indeterminate while only part of its family is selected. */
  isPartiallySelected(product: any): boolean {
    const family = this.family(product);
    const count = family.filter((item) => this.isSelected(item)).length;
    return count > 0 && count < family.length;
  }

  toggleVariation(variation: any, parent: any) {
    if (this.isSelected(variation)) {
      this.drop(variation);
      // A parent with no remaining variations selected is no longer a match.
      const anyLeft = (parent.variations || []).some((item) =>
        this.isSelected(item)
      );
      if (!anyLeft) {
        this.drop(parent);
      }
    } else {
      this.add(variation);
      this.add(parent);
    }
    this.selectedChange.emit(this.selected);
  }

  toggleExpand(product: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.expandedElement = this.isExpanded(product) ? null : product.id;
  }

  isExpanded(product: any): boolean {
    return (
      this.expandedElement !== null &&
      this.sameId(this.expandedElement, product.id)
    );
  }

  hasVariations(product: any): boolean {
    return !!(product.variations && product.variations.length);
  }

  // ------------------------------------------------------------- bulk moves

  get pageSelectedCount(): number {
    return this.dataSource.data.filter((item) => this.isSelected(item)).length;
  }

  get isPageSelected(): boolean {
    return (
      this.dataSource.data.length > 0 &&
      this.pageSelectedCount === this.dataSource.data.length
    );
  }

  get isPagePartiallySelected(): boolean {
    const count = this.pageSelectedCount;
    return count > 0 && count < this.dataSource.data.length;
  }

  togglePage() {
    const shouldSelect = !this.isPageSelected;
    this.dataSource.data.forEach((item) => this.setSelected(item, shouldSelect));
  }

  /** Offered once the page is fully selected and more rows match the filter. */
  get canSelectAllMatching(): boolean {
    return this.isPageSelected && this.dataLength > this.dataSource.data.length;
  }

  selectAllMatching() {
    this.bulkLoading = true;
    this.eProductService
      .getAllProductsList({
        pagination: { pageSize: this.dataLength, pageNumber: 0 },
        searchText: this.searchText || "",
        categoryIds: this.selectedCategoryIds || [],
      })
      .subscribe(
        (res: any) => {
          (res.data || []).forEach((item) => this.setSelected(item, true));
          this.bulkLoading = false;
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.bulkLoading = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  // ------------------------------------------------------------------- tray

  get visibleChips(): SelectedProduct[] {
    return this.trayExpanded
      ? this.selected
      : this.selected.slice(0, this.chipLimit);
  }

  removeChip(item: SelectedProduct) {
    this.drop(item);
    this.selectedChange.emit(this.selected);
  }

  clearAll() {
    this.selected.splice(0, this.selected.length);
    this.trayExpanded = false;
    this.selectedChange.emit(this.selected);
  }

  trackById(_index: number, item: any) {
    return item.id;
  }
}
